// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Procedural formant speech (M3-27, 07 §4).
//
// NEON NOVA's callouts are spoken by the ship's AI, and 07 §4 says what the AI is: a
// **source-filter formant synthesizer**. "This is not a compromise: a synthetic voice is the
// thematically correct voice for this machine, and it means zero recorded audio, zero
// voice-talent licensing, and full localisation later."
//
// The model is the oldest one in speech synthesis and still the clearest. A larynx makes a
// buzz; a tract shapes it. So:
//
//   source  — a glottal pulse train at 95–130 Hz with 2% jitter and 1.5% shimmer, plus a noise
//             source mixed in for the fricatives
//   filter  — five parallel bandpass formants whose centres and bandwidths move between phoneme
//             targets over 40 ms
//   prosody — pitch and duration from the authored emphasis marks, over a falling contour
//   colour  — a light ring modulator and a short comb, which is what makes it a ship's AI
//             rather than a person
//
// **What makes it intelligible is almost entirely the third and fourth lines of the table, not
// the first two.** Formants at the right frequencies with no prosody produce something a
// listener can decode letter by letter and cannot follow; the same formants with stress, a
// declining contour and phrase-final lengthening produce a sentence. And the plosives matter
// far more than their duration suggests: a stop is *silence* and then a release, and a
// synthesizer that leaves the silence out runs every word into the next one.
//
// Rendered to a buffer like everything else in this layer (07 §1 rule 5). A callout is over a
// second long and there are ten of them, which is why they are the one family the bank renders
// after the machine is playable rather than before — see `speechBank.ts`.

import { Rng } from '@core/rng/Rng'
import {
  Biquad,
  combInPlace,
  fadeOut,
  GLIDE_BLOCK,
  normalise,
  samplesFor,
  type NoiseSource,
  type Samples,
} from './dsp'
import {
  FORMANT_COUNT,
  isNucleus,
  parsePhonemes,
  syllables,
  TRANSITION_MS,
  type Formant,
  type Phoneme,
} from './phonemes'

/** A callout as a machine definition authors it (04, `audio.speech`). */
export interface SpeechLine {
  /** The caption, required by NFR-022. Never fed to a synthesizer — see `phonemes.ts`. */
  readonly text: string
  readonly phonemes: string
  /** Which syllables carry stress, by index. */
  readonly emphasis: readonly number[]
}

/**
 * A voice.
 *
 * One profile today — the ship's AI — because one machine exists. It is a table rather than a
 * set of constants because 07 §4 has localisation and other machines in view, and because the
 * numbers below are the only place the character of the voice lives: change `ringHz` and it is
 * a different machine's computer.
 */
export interface VoiceProfile {
  readonly name: string
  /** Middle of the pitch contour, Hz, before a machine's own `pitch` scales it. */
  readonly baseHz: number
  /** How far the contour falls across an utterance, either side of the middle. */
  readonly declination: number
  /** How much a stressed syllable lifts the pitch. */
  readonly stressLift: number
  /** Period-to-period wobble, and pulse-to-pulse amplitude wobble (07 §4: 2% and 1.5%). */
  readonly jitter: number
  readonly shimmer: number
  /** Scales every formant. Below 1 is a longer tract — a larger, slower speaker. */
  readonly tractScale: number
  /** Breath mixed into voiced segments. A tract with no noise in it sounds like an organ. */
  readonly breath: number
  /** The ring modulator: a metallic edge that no human larynx has. */
  readonly ringHz: number
  readonly ringDepth: number
  /** A short comb — the sound of a voice arriving through a small enclosure. */
  readonly combHz: number
  readonly combFeedback: number
}

/**
 * The pitch bounds of 07 §4.
 *
 * A contour is built to sit inside them rather than clamped into them, but a machine may set
 * its own `pitch` and the clamp is what keeps a mistyped value a slightly odd voice instead of
 * a chipmunk or a foghorn.
 */
export const PITCH_FLOOR_HZ = 95
export const PITCH_CEILING_HZ = 130

export const VOICE_PROFILES: Readonly<Record<string, VoiceProfile>> = {
  /**
   * The ship's AI.
   *
   * Deliberately close to the bottom of the range: a low voice with a light ring modulator
   * reads as a machine, while the same modulator on a high one reads as a toy. The comb is
   * eight milliseconds, which is a box about the size of a backbox.
   */
  shipAi: {
    name: 'shipAi',
    baseHz: 110,
    declination: 0.05,
    stressLift: 0.14,
    jitter: 0.02,
    shimmer: 0.015,
    tractScale: 0.98,
    breath: 0.06,
    ringHz: 62,
    ringDepth: 0.16,
    combHz: 125,
    combFeedback: 0.22,
  },
}

/** Fallback when a machine names a profile that does not exist. Reported, never silent. */
export const DEFAULT_PROFILE = 'shipAi'

/** How long a stressed syllable's nucleus is held, against an unstressed one. */
const STRESS_STRETCH = 1.3

/** Phrase-final lengthening. The last syllable of a statement is held, and it reads as an end. */
const FINAL_STRETCH = 1.18

/** How fast the formants chase their target. 07 §4's 40 ms, as a time constant. */
const FORMANT_TAU_MS = TRANSITION_MS / 2.3

/** How fast the amplitude chases its target. Far shorter: a plosive burst is twelve milliseconds. */
const LEVEL_TAU_MS = 4

/** Silence at the end, so a callout does not butt up against whatever plays next. */
const TAIL_MS = 60

/** One phoneme, placed in time. */
interface Segment {
  readonly phoneme: Phoneme
  readonly startSample: number
  readonly closureSamples: number
  readonly voicedSamples: number
  readonly stressed: boolean
}

/** A callout's plan: what is said, when, and with what stress. */
export interface SpeechPlan {
  readonly segments: readonly Segment[]
  readonly totalSamples: number
  readonly syllableCount: number
}

/**
 * Lay a callout out in time.
 *
 * Separate from rendering because the timing is the half that can be *read*: a test can assert
 * that "Big bang" is two syllables, that both are stressed, and that a stressed syllable is
 * longer than an unstressed one, without looking at a single sample.
 */
export function planCallout(line: SpeechLine, sampleRate: number): SpeechPlan {
  const phonemes = parsePhonemes(line.phonemes)
  const groups = syllables(phonemes)
  const stressedPhoneme = new Set<number>()
  for (const index of line.emphasis) {
    for (const i of groups[index] ?? []) stressedPhoneme.add(i)
  }
  const lastGroup = groups[groups.length - 1] ?? []

  const segments: Segment[] = []
  let at = 0
  for (let i = 0; i < phonemes.length; i++) {
    const phoneme = phonemes[i]!
    const stressed = stressedPhoneme.has(i)
    let scale = 1
    // Only the nucleus stretches. Stretching a syllable's consonants as well makes a stressed
    // word sound slow rather than emphatic — the difference between "BIG bang" and "biiig bang".
    if (stressed && isNucleus(phoneme)) scale *= STRESS_STRETCH
    if (isNucleus(phoneme) && lastGroup.includes(i)) scale *= FINAL_STRETCH

    // `samplesFor` floors at one sample, which is right for a buffer length and wrong for a
    // duration that is genuinely zero — it gave every vowel a one-sample closure.
    const closure = phoneme.closureMs > 0 ? samplesFor(phoneme.closureMs, sampleRate) : 0
    const body = samplesFor((phoneme.burstMs > 0 ? phoneme.burstMs : phoneme.ms) * scale, sampleRate)
    segments.push({
      phoneme,
      startSample: at,
      closureSamples: closure,
      voicedSamples: Math.max(1, body),
      stressed,
    })
    at += closure + Math.max(1, body)
  }
  return { segments, totalSamples: at, syllableCount: groups.length }
}

/** How long a callout runs, milliseconds. What the caption has to stay on screen for. */
export function calloutLengthMs(line: SpeechLine, sampleRate: number): number {
  return ((planCallout(line, sampleRate).totalSamples + samplesFor(TAIL_MS, sampleRate)) / sampleRate) * 1000
}

/**
 * The glottal source, as a table: **the derivative of the Rosenberg pulse, not the pulse.**
 *
 * The pulse is the airflow through the folds — a smooth opening, a faster closing, a closed
 * phase. What leaves the lips is its *derivative*, because radiation from a small opening
 * differentiates, and that is where speech's 6 dB per octave tilt comes from and why the
 * formants are excited at all: the derivative has a sharp negative spike at closure and the
 * flow itself has nothing sharp in it anywhere.
 *
 * **Differentiating in the time domain instead is a trap, and this code fell into it.** A first
 * difference of a waveform that takes four hundred samples to rise gives an amplitude of about
 * one four-hundredth, while the noise source is ±1 per sample — so a fricative came out fifty
 * times louder than a vowel, every callout normalised to its own consonants, and the vowels
 * were nearly inaudible under them. Differentiating the *shape* once, here, and normalising it
 * removes the dependence on f0 entirely: a hundred-hertz voice and a two-hundred-hertz voice
 * excite the tract equally hard, which is also what a larynx does.
 *
 * Tabulated because the shape never changes — only the rate it is read at — so evaluating it
 * per sample would be a hundred thousand cosines computing the same five hundred numbers. It
 * is also what makes the C# core's source bit-identical to this one rather than identical to
 * within its platform's `cos`.
 */
const PULSE_POINTS = 512
const PULSE = buildPulse()

function buildPulse(): Float64Array {
  // Open quotient 0.6: the folds are apart for 60% of the period, which is a modal voice —
  // neither breathy nor pressed.
  const open = 0.36
  const close = 0.24
  const flow = new Float64Array(PULSE_POINTS + 2)
  for (let i = 0; i < flow.length; i++) {
    const t = i / PULSE_POINTS
    if (t < open) flow[i] = 0.5 * (1 - Math.cos((Math.PI * t) / open))
    else if (t < open + close) flow[i] = Math.cos((Math.PI * (t - open)) / (2 * close))
    else flow[i] = 0
  }

  const out = new Float64Array(PULSE_POINTS + 1)
  let peak = 0
  for (let i = 0; i <= PULSE_POINTS; i++) {
    out[i] = flow[i + 1]! - flow[i]!
    peak = Math.max(peak, Math.abs(out[i]!))
  }
  if (peak > 0) for (let i = 0; i <= PULSE_POINTS; i++) out[i]! /= peak

  // **Unit RMS, not unit peak.** The two sources have to arrive at the filter bank equally
  // loud, or the phoneme table's `level` column means something different for a vowel than for
  // a fricative and every /s/ in the machine is louder than the vowel beside it. A glottal
  // derivative is a spike train with a low duty cycle and noise is on all the time, so
  // matching their peaks leaves them about ten decibels apart.
  let power = 0
  for (let i = 0; i < PULSE_POINTS; i++) power += out[i]! * out[i]!
  const level = Math.sqrt(power / PULSE_POINTS)
  if (level > 0) for (let i = 0; i <= PULSE_POINTS; i++) out[i]! /= level
  return out
}

/**
 * What a unit-variance noise source is scaled by.
 *
 * `noiseFrom` draws uniformly on ±1, whose RMS is 1/√3. Scaling it here rather than asking for
 * a different noise source keeps one generator behind every stochastic sound in the product.
 */
const NOISE_GAIN = Math.sqrt(3)

function pulseAt(phase: number): number {
  const x = phase * PULSE_POINTS
  const i = x | 0
  const frac = x - i
  const a = PULSE[i] ?? 0
  const b = PULSE[i + 1] ?? 0
  return a + (b - a) * frac
}

/**
 * Render a callout.
 *
 * `pitch` is the machine's own multiplier on the profile's base frequency (04's `audio.voice`).
 * `rng` drives the jitter and shimmer, which are the difference between a voice and a test
 * tone; it is the **audio** generator, never the simulation's (07 rule 4).
 */
export function renderCallout(
  line: SpeechLine,
  profile: VoiceProfile,
  pitch: number,
  sampleRate: number,
  noise: NoiseSource,
  rng: Rng,
): Samples {
  const plan = planCallout(line, sampleRate)
  const tail = samplesFor(TAIL_MS, sampleRate)
  const out = new Float32Array(plan.totalSamples + tail)
  if (plan.segments.length === 0) return out

  const filters: Biquad[] = []
  for (let f = 0; f < FORMANT_COUNT; f++) filters.push(new Biquad())

  // Smoothed parameters. Everything the tract does is continuous, so nothing here steps.
  const hz = new Float64Array(FORMANT_COUNT)
  const bw = new Float64Array(FORMANT_COUNT)
  const gain = new Float64Array(FORMANT_COUNT)
  let voiced = 0
  let noisy = 0
  let level = 0

  const formantChase = blockChase(FORMANT_TAU_MS, sampleRate)
  const levelChase = blockChase(LEVEL_TAU_MS, sampleRate)

  // Start the formants at the first segment's targets rather than at zero, or every callout
  // opens with a swoop up from DC that is not a speech sound at all.
  const opening = targetsOf(plan.segments[0]!, 0, profile)
  for (let f = 0; f < FORMANT_COUNT; f++) {
    hz[f] = opening.formants[f]!.hz
    bw[f] = opening.formants[f]!.bandwidthHz
  }

  const base = clamp(profile.baseHz * pitch, PITCH_FLOOR_HZ, PITCH_CEILING_HZ)
  let glottalPhase = 0
  let period = sampleRate / base
  let shimmer = 1
  let segment = 0

  for (let start = 0; start < out.length; start += GLIDE_BLOCK) {
    const end = Math.min(start + GLIDE_BLOCK, out.length)
    while (
      segment + 1 < plan.segments.length &&
      start >= plan.segments[segment + 1]!.startSample
    ) {
      segment++
    }
    const current = plan.segments[segment]!
    const inTail = start >= plan.totalSamples
    const within = start - current.startSample
    const closed = within < current.closureSamples
    const through = closed
      ? 0
      : Math.min(1, (within - current.closureSamples) / current.voicedSamples)
    const target = targetsOf(current, through, profile)

    for (let f = 0; f < FORMANT_COUNT; f++) {
      hz[f]! += (target.formants[f]!.hz - hz[f]!) * formantChase
      bw[f]! += (target.formants[f]!.bandwidthHz - bw[f]!) * formantChase
      gain[f]! += (target.gains[f]! - gain[f]!) * formantChase
      filters[f]!.setBandpass(hz[f]!, Math.max(0.5, hz[f]! / bw[f]!), sampleRate)
    }

    // A closure is silence, and a *voiced* closure is the voicing bar — the low buzz you can
    // hear through a closed mouth, and the cue that separates "big" from "pick".
    const wantLevel = inTail ? 0 : closed ? current.phoneme.voiced * 0.07 : current.phoneme.level
    const wantVoiced = inTail ? 0 : closed ? 1 : current.phoneme.voiced
    const wantNoise = inTail || closed ? 0 : current.phoneme.noise
    level += (wantLevel - level) * levelChase
    voiced += (wantVoiced - voiced) * levelChase
    noisy += (wantNoise - noisy) * levelChase

    const f0 = contourHz(base, profile, start / plan.totalSamples, current.stressed)
    const nominal = sampleRate / f0

    for (let i = start; i < end; i++) {
      glottalPhase += 1 / period
      if (glottalPhase >= 1) {
        glottalPhase -= 1
        // A new period, and the only two places a voice is not periodic: 07 §4's 2% jitter in
        // the length and 1.5% shimmer in the height. Without them the source is a buzzer, and
        // the ear identifies a buzzer instantly however good the formants are.
        period = nominal * (1 + profile.jitter * (rng.nextFloat() * 2 - 1))
        shimmer = 1 + profile.shimmer * (rng.nextFloat() * 2 - 1)
      }
      // Already differentiated, and already at unit peak — see `buildPulse`.
      const flow = pulseAt(glottalPhase) * shimmer
      const hiss = noise.next() * NOISE_GAIN
      const source = voiced * (flow + profile.breath * hiss) + noisy * hiss

      let sum = 0
      for (let f = 0; f < FORMANT_COUNT; f++) sum += gain[f]! * filters[f]!.process(source)
      out[i] = level * sum
    }
  }

  colour(out, profile, sampleRate)
  fadeOut(out, sampleRate, 12)
  normalise(out)
  return out
}

/** The formant targets and gains a segment wants, `through` of the way into it. */
function targetsOf(
  segment: Segment,
  through: number,
  profile: VoiceProfile,
): { formants: readonly Formant[]; gains: readonly number[] } {
  const phoneme = segment.phoneme
  const from = phoneme.formants
  const to = phoneme.glide
  const formants: Formant[] = []
  for (let f = 0; f < FORMANT_COUNT; f++) {
    const a = from[f] ?? { hz: 500, bandwidthHz: 100 }
    const b = to?.[f] ?? a
    formants.push({
      // A diphthong is one phoneme with a moving tongue. Writing it as two would put a
      // transition in the middle of a sound that has no boundary in it.
      hz: (a.hz + (b.hz - a.hz) * through) * profile.tractScale,
      bandwidthHz: a.bandwidthHz + (b.bandwidthHz - a.bandwidthHz) * through,
    })
  }
  return { formants, gains: phoneme.gains }
}

/**
 * The pitch at a point in the utterance.
 *
 * Two things, and both are prosody rather than decoration. **Declination** — pitch falls across
 * a statement, and a flat contour is the single most robotic thing a synthesizer can do, in the
 * bad sense rather than this machine's sense. **Stress** — a marked syllable is higher as well
 * as longer, and doing only the length makes it sound laboured instead of emphatic.
 */
function contourHz(base: number, profile: VoiceProfile, through: number, stressed: boolean): number {
  const fall = 1 + profile.declination * (1 - 2 * Math.min(1, Math.max(0, through)))
  const lift = stressed ? 1 + profile.stressLift : 1
  return clamp(base * fall * lift, PITCH_FLOOR_HZ, PITCH_CEILING_HZ * 1.1)
}

/**
 * The ship, over the voice.
 *
 * A ring modulator at 62 Hz puts sidebands either side of every harmonic that no vocal tract
 * produces, which is the whole of the "computer voice" effect and has been since 1963. Light,
 * because at any depth over about a quarter it stops being a voice with a machine in it and
 * becomes a machine with a voice in it, and the words go.
 */
function colour(out: Samples, profile: VoiceProfile, sampleRate: number): void {
  const step = (2 * Math.PI * profile.ringHz) / sampleRate
  const cosStep = Math.cos(step)
  const sinStep = Math.sin(step)
  let cos = 1
  let sin = 0
  for (let i = 0; i < out.length; i++) {
    out[i]! *= 1 - profile.ringDepth + profile.ringDepth * cos
    const next = cos * cosStep - sin * sinStep
    sin = sin * cosStep + cos * sinStep
    cos = next
    // The rotation is norm-preserving in real arithmetic and very slightly not in binary64.
    // Over a second and a half that is audible as a drifting depth, so it is renormalised.
    if ((i & (GLIDE_BLOCK - 1)) === 0) {
      const norm = Math.sqrt(cos * cos + sin * sin)
      if (norm > 0) {
        cos /= norm
        sin /= norm
      }
    }
  }
  combInPlace(out, sampleRate / profile.combHz, profile.combFeedback, 0.45)
}

/** How far a smoothed value moves in one block, for a given time constant. */
function blockChase(tauMs: number, sampleRate: number): number {
  const blocksPerTau = (tauMs * 0.001 * sampleRate) / GLIDE_BLOCK
  return blocksPerTau <= 0 ? 1 : Math.min(1, 1 / blocksPerTau)
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v
}
